import React, { useEffect, useState } from 'react';
import { ArrowLeft, CreditCard, Trash2, CheckCircle2 } from 'lucide-react';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import api from '../lib/api';
import FeatureHeader from './FeatureHeader';

export default function SubscriptionBilling({ onBack }) {
  const stripe = useStripe();
  const elements = useElements();

  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    cardholderName: '',
    isDefault: false,
  });

  const fetchCards = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/api/payment-cards');
      setCards(res.data?.cards || []);
    } catch (err) {
      setError('Failed to load saved cards.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCards();
  }, []);

  const handleAddCard = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.cardholderName.trim()) {
      setError('Cardholder name is required.');
      return;
    }

    if (!stripe || !elements) {
      setError('Payment service is not ready. Please try again in a moment.');
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setError('Card input is not available.');
      return;
    }

    const pmResult = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
      billing_details: {
        name: form.cardholderName.trim(),
      },
    });

    if (pmResult.error || !pmResult.paymentMethod?.id) {
      setError(pmResult.error?.message || 'Unable to tokenize card.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post('/api/payment-cards', {
        cardholderName: form.cardholderName.trim(),
        paymentMethodId: pmResult.paymentMethod.id,
        isDefault: form.isDefault,
      });

      const saved = res.data?.card;
      setCards((prev) => {
        const cleaned = form.isDefault ? prev.map((c) => ({ ...c, isDefault: false })) : prev;
        return saved ? [saved, ...cleaned] : cleaned;
      });

      setForm({ cardholderName: '', isDefault: false });
      cardElement.clear();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save card.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    setError('');
    try {
      await api.delete(`/api/payment-cards/${id}`);
      await fetchCards();
    } catch (err) {
      setError('Failed to remove card.');
    }
  };

  const handleSetDefault = async (id) => {
    setError('');
    try {
      await api.patch(`/api/payment-cards/${id}/default`);
      setCards((prev) => prev.map((c) => ({ ...c, isDefault: c._id === id })));
    } catch (err) {
      setError('Failed to set default card.');
    }
  };

  return (
    <div style={pageStyle}>
      <FeatureHeader title="Billing" onBack={onBack} />

      <div style={infoCardStyle}>
        <h2 style={{ fontSize: '1rem', fontWeight: '600' }}>Current Plan</h2>
        <p style={{ fontSize: '0.85rem', marginTop: 4 }}>Qureo</p>
        <p style={{ fontSize: '0.75rem', color: '#666' }}>Manage your billing cards for upcoming renewals.</p>
      </div>

      <h2 style={{ fontSize: '1rem', fontWeight: '600', marginTop: 24 }}>Add Card</h2>
      <form onSubmit={handleAddCard} style={formCardStyle}>
        <div style={formGridStyle}>
          <div>
            <label style={labelStyle}>Cardholder Name</label>
            <input
              style={inputStyle}
              value={form.cardholderName}
              onChange={(e) => setForm((prev) => ({ ...prev, cardholderName: e.target.value }))}
              placeholder="Name on card"
            />
          </div>

          <div>
            <label style={labelStyle}>Card Number</label>
            <div style={cardElementWrapStyle}>
              <CardElement
                options={{
                  style: {
                    base: {
                      fontSize: '14px',
                      color: '#111827',
                      '::placeholder': { color: '#9ca3af' },
                    },
                    invalid: { color: '#b42318' },
                  },
                }}
              />
            </div>
          </div>
        </div>

        <label style={checkboxStyle}>
          <input
            type="checkbox"
            checked={form.isDefault}
            onChange={(e) => setForm((prev) => ({ ...prev, isDefault: e.target.checked }))}
          />
          Set as default card
        </label>

        <button type="submit" style={actionBtnStyle} disabled={submitting}>
          {submitting ? 'Saving...' : 'Save Card'}
        </button>
      </form>

      {error && <div style={errorStyle}>{error}</div>}

      <h2 style={{ fontSize: '1rem', fontWeight: '600', marginTop: 20 }}>Saved Cards</h2>
      <div style={tableWrapStyle}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Card</th>
              <th style={thStyle}>Holder</th>
              <th style={thStyle}>Expiry</th>
              <th style={thStyle}>Default</th>
              <th style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} style={tdStyle}>Loading cards...</td>
              </tr>
            ) : cards.length === 0 ? (
              <tr>
                <td colSpan={5} style={tdStyle}>No saved cards yet.</td>
              </tr>
            ) : (
              cards.map((card) => (
                <tr key={card._id}>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <CreditCard size={16} style={{ color: 'var(--primary)' }} />
                      <span>{String(card.brand || 'card').toUpperCase()} ending in {card.last4}</span>
                    </div>
                  </td>
                  <td style={tdStyle}>{card.cardholderName}</td>
                  <td style={tdStyle}>{String(card.expMonth).padStart(2, '0')}/{card.expYear}</td>
                  <td style={tdStyle}>
                    {card.isDefault ? <CheckCircle2 size={16} color="#16a34a" /> : '-'}
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {!card.isDefault && (
                        <button style={smallBtnStyle} onClick={() => handleSetDefault(card._id)}>
                          Set Default
                        </button>
                      )}
                      <button style={dangerBtnStyle} onClick={() => handleDelete(card._id)}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const pageStyle = {
  minHeight: '100vh',
  padding: '24px 16px',
  background: 'var(--bg)',
  fontFamily: 'sans-serif',
  color: 'var(--on-bg)',
};

const headerRowStyle = {
  display: 'flex',
  alignItems: 'center',
  marginBottom: 16,
};

const backBtnStyle = {
  background: 'transparent',
  border: 'none',
  padding: 0,
  marginRight: 8,
  cursor: 'pointer',
};

const headerStyle = {
  fontSize: '1.25rem',
  fontWeight: '700',
};

const infoCardStyle = {
  background: 'var(--surface)',
  borderRadius: 12,
  padding: 16,
  boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
};

const formCardStyle = {
  marginTop: 10,
  background: 'var(--surface)',
  borderRadius: 12,
  padding: 16,
  boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
};

const formGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
  gap: 10,
};

const labelStyle = {
  fontSize: '0.78rem',
  fontWeight: 600,
  marginBottom: 4,
  display: 'block',
};

const inputStyle = {
  width: '100%',
  padding: '9px 10px',
  borderRadius: 8,
  border: '1px solid #d0d5dd',
  fontSize: '0.86rem',
};

const cardElementWrapStyle = {
  width: '100%',
  padding: '11px 10px',
  borderRadius: 8,
  border: '1px solid #d0d5dd',
  background: '#fff',
};

const checkboxStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginTop: 12,
  fontSize: '0.85rem',
};

const actionBtnStyle = {
  marginTop: 12,
  padding: '10px 14px',
  background: 'var(--primary)',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  fontSize: '0.85rem',
  fontWeight: 600,
  cursor: 'pointer',
};

const errorStyle = {
  marginTop: 10,
  color: '#b42318',
  fontSize: '0.85rem',
};

const tableWrapStyle = {
  marginTop: 10,
  overflowX: 'auto',
  background: 'var(--surface)',
  borderRadius: 12,
  boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  minWidth: 680,
};

const thStyle = {
  textAlign: 'left',
  padding: '10px 12px',
  fontSize: '0.78rem',
  fontWeight: 700,
  color: '#475467',
  borderBottom: '1px solid #eaecf0',
  background: '#f8fafc',
};

const tdStyle = {
  padding: '10px 12px',
  fontSize: '0.84rem',
  color: '#111827',
  borderBottom: '1px solid #f1f5f9',
};

const smallBtnStyle = {
  padding: '6px 10px',
  border: '1px solid #d0d5dd',
  borderRadius: 8,
  background: '#fff',
  fontSize: '0.75rem',
  cursor: 'pointer',
};

const dangerBtnStyle = {
  padding: '6px 8px',
  border: '1px solid #fecaca',
  borderRadius: 8,
  background: '#fff1f2',
  color: '#b42318',
  cursor: 'pointer',
};
